import { useUsersAction } from '../state/Users.actions';
import { useUsersSelector } from '../state/Users.context';
import { ValidationUtils } from '@orthly/runtime-utils';
import { SimpleInput, SimplePhoneInput } from '@orthly/ui';
import { Grid, InputAdornment, IconButton, Icon, Tooltip, Text } from '@orthly/ui-primitives';
import { useCopyToClipboard } from '@orthly/veneer';
import _ from 'lodash';
import React from 'react';

export interface EditUserCommonFieldsProps {
    setRequiredFieldsCompleted: (value: boolean) => void;
}

function randomPassword() {
    const baseLength = 10;
    // will generate a random password like: flavjzu523
    const basePassword = _.times(baseLength, () => _.random(35).toString(36)).join('');
    // and now we randomly generate an uppercased letter
    const randomUppercase = (_.random(26) + 10).toString(36).toUpperCase();
    const index = _.random(baseLength);
    return `${basePassword.substr(0, index)}${randomUppercase}${basePassword.substr(index)}`;
}

export const EditUserCommonFields: React.FC<EditUserCommonFieldsProps> = props => {
    const { setRequiredFieldsCompleted } = props;
    const updateField = useUsersAction('UPDATE_COMMON_FIELDS');
    const setPassword = useUsersAction('UPDATE_PASSWORD');
    const toggleEditingPassword = useUsersAction('TOGGLE_PASSWORD');
    const { data, user } = useUsersSelector(s => ({ data: s.form, user: s.user }));
    const copyToClipboard = useCopyToClipboard();

    React.useEffect(() => {
        if (data && data.first_name && data.last_name && data.email && ValidationUtils.isEmail(data?.email)) {
            setRequiredFieldsCompleted(true);
        } else {
            setRequiredFieldsCompleted(false);
        }
    }, [data, setRequiredFieldsCompleted]);

    if (!data) {
        return null;
    }
    // disables autofill
    const defaultTextFieldProps = { InputProps: { autoComplete: 'new-password' } };
    return (
        <Grid container direction={'column'}>
            <Grid item>
                <Text variant={'h6'}>About</Text>
            </Grid>
            <Grid container spacing={3} item direction={'row'}>
                <Grid item container spacing={2} direction={'column'} xs={6}>
                    <Grid item>
                        <SimpleInput
                            fullWidth
                            label={'First Name'}
                            value={data.first_name}
                            onChange={value => updateField({ property: 'first_name', value: value ?? '' })}
                            TextFieldProps={defaultTextFieldProps}
                            required
                        />
                    </Grid>
                    <Grid item>
                        <SimpleInput
                            fullWidth
                            label={'Last Name'}
                            value={data.last_name}
                            onChange={value => updateField({ property: 'last_name', value: value ?? '' })}
                            TextFieldProps={defaultTextFieldProps}
                            required
                        />
                    </Grid>
                    <Grid item>
                        <SimpleInput
                            fullWidth
                            label={'Login Email'}
                            value={data.email}
                            onChange={value => updateField({ property: 'email', value: value ?? '' })}
                            TextFieldProps={defaultTextFieldProps}
                            required
                        />
                    </Grid>
                </Grid>
                <Grid item container spacing={2} direction={'column'} xs={6}>
                    <Grid item>
                        <SimplePhoneInput
                            fullWidth
                            label={'Contact Phone Number'}
                            value={data.phone_number ?? ''}
                            onChange={value => updateField({ property: 'phone_number', value: value ?? undefined })}
                            TextFieldProps={defaultTextFieldProps}
                        />
                    </Grid>
                    <Grid item>
                        <SimplePhoneInput
                            fullWidth
                            label={'Mobile Login Phone Number'}
                            value={data.mobile_phone_number ?? ''}
                            onChange={value =>
                                updateField({ property: 'mobile_phone_number', value: !!value ? value : undefined })
                            }
                            TextFieldProps={defaultTextFieldProps}
                        />
                    </Grid>
                    <Grid item>
                        <SimpleInput
                            fullWidth
                            label={'Password'}
                            value={!user || data.password.visible ? data.password.value : '****************'}
                            onChange={password => setPassword({ password: password ?? '' })}
                            TextFieldProps={{
                                disabled: user && !data.password.visible,
                                InputProps: {
                                    autoComplete: 'new-password',
                                    endAdornment: (
                                        <InputAdornment position={'end'} style={{ height: 30, paddingBottom: 4 }}>
                                            <IconButton onClick={toggleEditingPassword}>
                                                {user && !data.password.visible && <Icon icon={'LockOutlined'} />}
                                                {user && data.password.visible && <Icon icon={'LockOpenOutlined'} />}
                                            </IconButton>
                                            {data.password.visible && (
                                                <Tooltip title={'Generate a password'}>
                                                    <IconButton
                                                        onClick={() => {
                                                            const newPassword = randomPassword();

                                                            setPassword({ password: newPassword });

                                                            copyToClipboard({
                                                                text: newPassword,
                                                                successMessage: `Auto-generated password copied to clipboard!`,
                                                                errorMessage: `Failed to copy auto-generated password :(`,
                                                            });
                                                        }}
                                                    >
                                                        <Icon icon={'VpnKey'} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
